import React, { useEffect } from "react";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import "./PrivacyPolicy.scss";

function PrivacyPolicy() {
  return (
    <>
      <Header />
      
      <section className="privacy-page">
        <div className="privacy-content">
            <h1 className="privacy-title">Privacy Policy</h1>
            <p className="privacy-last-updated">LAST UPDATED: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            
            <div className="privacy-intro">
              <p>
                This Privacy Policy describes how Talsolution ("Talsolution," "we," "us," or "our") collects, uses, and shares your personal 
                information when you use our website, services, and platform (collectively, the "Site" or "Service"). We are committed to 
                protecting your privacy and ensuring the security of your personal information.
              </p>
              <p>
                By using our Site, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not 
                agree with our policies and practices, please do not use our Site.
              </p>
            </div>

            <div className="privacy-section">
              <h2>1. Information We Collect</h2>
              
              <h3>1.1 Information You Provide to Us</h3>
              <p>We collect information that you provide directly to us through:</p>
              <ul className="privacy-list">
                <li><strong>Contact Forms:</strong> When you contact us through our website contact forms</li>
                <li><strong>Job Applications:</strong> When you apply for positions through our platform</li>
                <li><strong>Account Registration:</strong> If you create an account on our platform</li>
              </ul>
              <p>The specific information we collect includes:</p>
              <ul className="privacy-list">
                <li><strong>Account Information:</strong> Name, email address, phone number, and other contact information</li>
                <li><strong>Application Information:</strong> Resume, cover letter, work history, education, skills, certifications, and other professional information</li>
                <li><strong>Video Recordings:</strong> Video introductions and responses you submit as part of the application process. By submitting video recordings, you explicitly consent to their storage, processing, and sharing with potential employers as described in this policy.</li>
                <li><strong>Profile Information:</strong> Professional background, certifications, portfolio links, and other information you choose to include</li>
                <li><strong>Communication Data:</strong> Messages, emails, and other communications you send to us or through our platform</li>
                <li><strong>Form Submissions:</strong> Any information you provide in forms on our website</li>
              </ul>

              <h3>1.2 Information We Collect Automatically</h3>
              <p>When you use our Site, we automatically collect certain information through:</p>
              <ul className="privacy-list">
                <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies. See our <a href="/cookiepolicy">Cookie Policy</a> for detailed information.</li>
                <li><strong>Server Logs:</strong> Our servers automatically log information about your visit</li>
                <li><strong>Analytics Tools:</strong> Third-party analytics services may collect information about your usage</li>
              </ul>
              <p>The specific information collected automatically includes:</p>
              <ul className="privacy-list">
                <li><strong>Usage Data:</strong> Pages viewed, time spent on pages, click paths, search queries, features used, and actions taken</li>
                <li><strong>Device Information:</strong> IP address, browser type and version, operating system, device type, screen resolution, and device identifiers</li>
                <li><strong>Location Data:</strong> General location information derived from your IP address (city/region level, not precise location)</li>
                <li><strong>Referral Information:</strong> The website or source that referred you to our Site</li>
              </ul>

              <h3>1.3 Information from Third Parties</h3>
              <p>
                We may receive information about you from third parties, such as employers, recruiters, or other users of our platform who 
                may provide feedback or evaluations.
              </p>
            </div>

            <div className="privacy-section">
              <h2>2. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul className="privacy-list">
                <li>To provide, maintain, and improve our services and platform</li>
                <li>To process and manage your job applications</li>
                <li>To match you with relevant job opportunities</li>
                <li>To share your application materials (including resumes and video recordings) with potential employers</li>
                <li>To communicate with you about your applications, account, and our services</li>
                <li>To send you updates, newsletters, and promotional materials (you can opt out at any time)</li>
                <li>To analyze and improve our platform's functionality and user experience</li>
                <li>To detect, prevent, and address technical issues, fraud, or security concerns</li>
                <li>To comply with legal obligations and enforce our Terms of Service</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>3. How We Share Your Information</h2>
              
              <h3>3.1 With Employers and Recruiters</h3>
              <p>
                When you apply for a position, we share your application materials, including your resume, cover letter, video recordings, 
                and other submitted information, with the employer or recruiter posting the position. This is necessary to facilitate the 
                hiring process.
              </p>

              <h3>3.2 With Service Providers</h3>
              <p>
                We may share your information with third-party service providers who perform services on our behalf, including:
              </p>
              <ul className="privacy-list">
                <li><strong>Cloud Storage:</strong> Amazon Web Services (AWS) S3 for storing resumes, video recordings, and other application materials</li>
                <li><strong>Analytics Services:</strong> We may use analytics tools such as Google Analytics to understand how visitors use our Site</li>
                <li><strong>Email Services:</strong> Third-party email service providers for sending communications</li>
                <li><strong>Hosting and Infrastructure:</strong> Cloud hosting providers for website and platform infrastructure</li>
              </ul>
              <p>
                These service providers are contractually obligated to protect your information and use it only for the purposes we specify. 
                They are not permitted to use your personal information for their own purposes.
              </p>

              <h3>3.3 Legal Requirements</h3>
              <p>
                We may disclose your information if required by law, court order, or governmental regulation, or if we believe disclosure 
                is necessary to protect our rights, property, or safety, or that of our users or others.
              </p>

              <h3>3.4 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, 
                subject to the same privacy protections.
              </p>

              <h3>3.5 With Your Consent</h3>
              <p>We may share your information with third parties when you have given us explicit consent to do so.</p>
            </div>

            <div className="privacy-section">
              <h2>4. Data Storage and Security</h2>
              
              <h3>4.1 Storage</h3>
              <p>
                Your information, including resumes and video recordings, is stored securely using industry-standard cloud storage services, 
                specifically Amazon Web Services (AWS) S3). We retain your information for as long as necessary to provide our services and 
                comply with legal obligations. Specifically:
              </p>
              <ul className="privacy-list">
                <li><strong>Application Materials:</strong> We retain resumes, cover letters, and video recordings for as long as necessary to 
                facilitate the hiring process and for a reasonable period thereafter for record-keeping purposes, typically up to 3 years after 
                your last activity on the platform.</li>
                <li><strong>Account Information:</strong> We retain account information while your account is active and for a period after 
                account closure as required by law or for legitimate business purposes.</li>
                <li><strong>Usage Data:</strong> We retain analytics and usage data in aggregated, anonymized form for platform improvement purposes.</li>
              </ul>
              <p>
                You may request deletion of your data at any time by contacting us at <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a>. 
                We will honor such requests subject to our legal obligations to retain certain information.
              </p>

              <h3>4.2 Security Measures</h3>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized 
                access, alteration, disclosure, or destruction, including:
              </p>
              <ul className="privacy-list">
                <li>Encryption of data in transit using SSL/TLS protocols</li>
                <li>Secure cloud storage infrastructure (AWS S3) with access controls</li>
                <li>Regular security assessments and monitoring</li>
                <li>Access controls limiting employee access to personal information on a need-to-know basis</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security. 
                You are responsible for maintaining the confidentiality of any account credentials you use to access our services.
              </p>
            </div>

            <div className="privacy-section">
              <h2>5. Your Rights and Choices</h2>
              
              <h3>5.1 Access and Correction</h3>
              <p>
                You have the right to access, update, and correct your personal information. You can do this by logging into your account 
                or contacting us at <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a>.
              </p>

              <h3>5.2 Deletion</h3>
              <p>
                You may request deletion of your account and personal information at any time. Please note that we may retain certain 
                information as required by law or for legitimate business purposes.
              </p>

              <h3>5.3 Opt-Out</h3>
              <p>
                You can opt out of receiving promotional communications from us by following the unsubscribe instructions in our emails or 
                by contacting us directly.
              </p>

              <h3>5.4 Data Portability</h3>
              <p>
                You have the right to receive a copy of your personal information in a structured, commonly used, and machine-readable format. 
                You may request this by contacting us at <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a>.
              </p>

              <h3>5.5 Cookies</h3>
              <p>
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain 
                features of our Site. For more information, please see our <a href="/cookiepolicy">Cookie Policy</a>.
              </p>
            </div>

            <div className="privacy-section">
              <h2>6. Children's Privacy</h2>
              <p>
                Our Site is not intended for individuals under the age of 18. We do not knowingly collect personal information from children 
                under 18. If you believe we have collected information from a child under 18, please contact us immediately, and we will 
                take steps to delete such information.
              </p>
            </div>

            <div className="privacy-section">
              <h2>7. International Data Transfers</h2>
              <p>
                Talsolution operates globally, and your information may be transferred to and processed in countries other than your country 
                of residence, including the United States where our servers are located. By using our Site, you consent to the transfer of 
                your information to these countries, which may have different data protection laws than your country.
              </p>
              <p>
                If you are located in the European Economic Area (EEA), United Kingdom, or other jurisdictions with specific data protection 
                requirements, we take appropriate safeguards to ensure your data is protected in accordance with applicable laws. These 
                safeguards may include standard contractual clauses approved by relevant authorities.
              </p>
            </div>

            <div className="privacy-section">
              <h2>8. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have certain rights under the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="privacy-list">
                <li>The right to know what personal information we collect, use, and share</li>
                <li>The right to delete your personal information</li>
                <li>The right to opt out of the sale of personal information (Talsolution does not sell personal information)</li>
                <li>The right to non-discrimination for exercising your privacy rights</li>
              </ul>
              <p>
                To exercise these rights, please contact us at <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a>.
              </p>
            </div>

            <div className="privacy-section">
              <h2>9. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy 
                Policy on this page and updating the "Last Updated" date. Your continued use of our Site after such changes constitutes your 
                acceptance of the updated Privacy Policy.
              </p>
            </div>

            <div className="privacy-section">
              <h2>10. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="privacy-contact">
                <p><strong>Talsolution</strong></p>
                <p>2821 Mexico Gravel Rd #5 Columbia MO 65202</p>
                <p>Email: <a href="mailto:privacy@talsolution.com">privacy@talsolution.com</a></p>
                <p>Phone: <a href="tel:+12108302282">+1 (210) 830-2282</a></p>
              </div>
            </div>
          </div>
      </section>

      <Footer />
      <Gotop />
    </>
  );
}

export default PrivacyPolicy;
